// frontend/src/pages/freelancer/FreelancerSettings.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import axios from "axios";
import { prepareImageForUpload } from "../../utils/imageUploadPolicy";
import "./freelancerStyles.css";
import EmailChangeModal from "../../components/EmailChangeModal";
import ImageUploadField from "../../components/ImageUploadField";

function getAgreementStatusLabel(status) {
  const map = {
    draft: "Draft",
    issued: "Issued",
    r7_signed: "Signed by Booth Renter",
    fully_executed: "Fully Executed",
    active: "Active",
    amendment_pending: "Amendment Pending",
    suspended: "Suspended",
    pending_reinstatement: "Pending Reinstatement",
    terminated: "Terminated",
    archived: "Archived",
  };

  return map[status] || status || "-";
}

function getAgreementEventLabel(event) {
  const map = {
    agreement_issued: "Agreement Issued",
    agreement_viewed: "Agreement Viewed",
    agreement_signed_r7: "Signed by Booth Renter",
    agreement_signed_r1: "Countersigned by Owner",
    agreement_executed: "Agreement Activated",
    policy_applied_due_date: "Policy Due Date Applied",
    late_fee_applied: "Late Fee Applied",
    suspension_applied: "Suspension Applied",
    reinstatement_requested: "Reinstatement Requested",
    reinstatement_approved: "Reinstatement Approved",
    offer_policy_updated: "Offer Policy Updated",
    agreement_terminated: "Agreement Terminated",
  };

  return map[event] || event || "-";
}

function getAgreementActorLabel(actorRole) {
  const map = {
    R1: "Owner",
    R7: "Booth Renter",
    system: "System",
    admin: "Admin",
  };

  return map[actorRole] || actorRole || "-";
}

function getAgreementDateLabel(agreement) {
  const value =
    agreement?.auditSummary?.r7SignedAt ||
    agreement?.auditSummary?.createdAt ||
    agreement?.r7Signature?.signedAt ||
    null;

  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function normalizeAgreementHtml(renderedHtml, status) {
  if (!renderedHtml || typeof renderedHtml !== "string") {
    return "";
  }

  const isExecuted = ["fully_executed", "active"].includes(status);
  const previewTextRegex =
    /This is a preview only and does not become enforceable until signing is complete\./gi;
  const templateVersionRegex =
    /<p[^>]*>\s*Template\s+BOOTH_RENTAL_v\d+\s*\|\s*Agreement Version\s*\d+\s*<\/p>/gi;

  return renderedHtml
    .replace(/\[R1\]/gi, "")
    .replace(/\[R7\]/gi, "")
    .replace(/\(\s*R1\s*\)/gi, "")
    .replace(/\(\s*R7\s*\)/gi, "")
    .replace(/Owner\s*\(\s*R1\s*\)\s*:/gi, "Owner:")
    .replace(/Renter\s*\(\s*R7\s*\)\s*:/gi, "Booth Renter:")
    .replace(/<strong>\s*Renter\s*:/gi, "<strong>Booth Renter:")
    .replace(templateVersionRegex, "")
    .replace(
      previewTextRegex,
      isExecuted
        ? "This agreement is fully executed and enforceable."
        : "This agreement becomes enforceable once signing is complete.",
    )
    .replace(/\s+:/g, ":")
    .replace(/\s{2,}/g, " ");
}

function FreelancerSettings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(
    searchParams.get("tab") || "account",
  );
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [agreementEvents, setAgreementEvents] = useState([]);
  const [loadingAgreements, setLoadingAgreements] = useState(false);
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    bookingReminders: true,
    marketingEmails: false,
    twoFactorAuth: false,
    publicProfile: true,
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [stripeStatus, setStripeStatus] = useState({
    loading: true,
    connected: false,
    needsOnboarding: false,
    chargesEnabled: false,
    payoutsEnabled: false,
  });
  const [selectedAccountType, setSelectedAccountType] = useState("individual");
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [downloadingData, setDownloadingData] = useState(false);
  const [brandImage, setBrandImage] = useState(null);
  const [brandImagePreview, setBrandImagePreview] = useState(null);
  const [uploadingBrandImage, setUploadingBrandImage] = useState(false);
  const [brandName, setBrandName] = useState("");
  const [savingBrandName, setSavingBrandName] = useState(false);
  const isBoothRenterPath = window.location.pathname.includes("/booth-renter/");
  const paymentMethodsPath = isBoothRenterPath
    ? "/dashboard/booth-renter/payment-methods"
    : "/dashboard/freelancer/payment-methods";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error.");
      return null;
    }
    return baseURL;
  }, []);

  const fetchSubscriptionData = useCallback(async () => {
    try {
      setLoadingSubscription(true);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const [statusRes, upcomingRes] = await Promise.all([
        axios.get(`${baseURL}/api/subscription-new/status`, { headers }),
        axios
          .get(`${baseURL}/api/subscription-new/upcoming-invoice`, { headers })
          .catch(() => null),
      ]);
      setSubscriptionData(statusRes.data);
      setUpcomingInvoice(upcomingRes?.data?.upcomingInvoice || null);
    } catch (err) {
    } finally {
      setLoadingSubscription(false);
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  const fetchAgreements = useCallback(async () => {
    if (!isBoothRenterPath) {
      return;
    }

    try {
      setLoadingAgreements(true);
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const listRes = await axios.get(`${baseURL}/api/booth-rental/contracts/r7`, {
        headers,
        params: { page: 1, limit: 20 },
      });

      const items = listRes.data?.items || [];
      setAgreements(items);

      if (items.length > 0) {
        const agreementId = items[0].agreementId;
        const [detailRes, eventsRes] = await Promise.all([
          axios.get(`${baseURL}/api/booth-rental/contracts/${agreementId}`, {
            headers,
          }),
          axios.get(`${baseURL}/api/booth-rental/contracts/${agreementId}/events`, {
            headers,
          }),
        ]);

        setSelectedAgreement(detailRes.data);
        setAgreementEvents(eventsRes.data?.events || []);
      } else {
        setSelectedAgreement(null);
        setAgreementEvents([]);
      }
    } catch (err) {
      setAgreements([]);
      setSelectedAgreement(null);
      setAgreementEvents([]);
    } finally {
      setLoadingAgreements(false);
    }
  }, [getAuthHeaders, getApiBaseUrl, isBoothRenterPath]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;

        // Get user data from localStorage
        const userStr = localStorage.getItem("ogestyle_user");
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            setUserData(user);
          } catch (err) {
          }
        }

        const res = await axios.get(`${baseURL}/api/staff/settings`, {
          headers,
        });
        if (res.data.settings) {
          setSettings(res.data.settings);
        }

        // Fetch staff profile for brand image
        const profileRes = await axios.get(
          `${baseURL}/api/freelancer/profile`,
          {
            headers,
          },
        );
        if (profileRes.data.staff?.brandImage) {
          setBrandImagePreview(profileRes.data.staff.brandImage);
        }
        setBrandName(profileRes.data.staff?.businessName || "");

        setLoading(false);
      } catch (err) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem("ogestyle_token");
          navigate("/login");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [getAuthHeaders, getApiBaseUrl, navigate]);

  useEffect(() => {
    const fetchStripeStatus = async () => {
      if (activeSection !== "payments") {
        return; // Only fetch when on payments tab
      }

      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;

        const res = await axios.get(`${baseURL}/api/freelancer/stripe/status`, {
          headers,
        });
        setStripeStatus({
          loading: false,
          connected: res.data.connected || false,
          needsOnboarding: res.data.needsOnboarding || false,
          chargesEnabled: res.data.chargesEnabled || false,
          payoutsEnabled: res.data.payoutsEnabled || false,
        });
      } catch (err) {
        setStripeStatus({
          loading: false,
          connected: false,
          needsOnboarding: false,
          chargesEnabled: false,
          payoutsEnabled: false,
        });
      }
    };

    // Check for return from Stripe onboarding
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("success") === "true" && activeSection === "payments") {
      fetchStripeStatus();
      // Clean URL
      window.history.replaceState(
        {},
        "",
        window.location.pathname + "?tab=payments",
      );
    } else {
      fetchStripeStatus();
    }
  }, [activeSection, getAuthHeaders, getApiBaseUrl]);

  useEffect(() => {
    if (activeSection === "subscription") {
      fetchSubscriptionData();
    }
  }, [activeSection, fetchSubscriptionData]);

  useEffect(() => {
    if (activeSection === "agreements") {
      fetchAgreements();
    }
  }, [activeSection, fetchAgreements]);

  const handleToggle = (key) => {
    setSettings({ ...settings, [key]: !settings[key] });
    setErrorMsg("");
    setSuccessMsg("");
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.patch(
        `${baseURL}/api/staff/settings`,
        { settings },
        { headers },
      );
      setSuccessMsg("Settings saved successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg(err?.response?.data?.message || "Failed to save settings");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrorMsg("Passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setErrorMsg("Password must be at least 8 characters");
      return;
    }

    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.patch(
        `${baseURL}/api/staff/change-password`,
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        },
        { headers },
      );

      setSuccessMsg("Password changed successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg(
          err?.response?.data?.message || "Failed to change password",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmMessage =
      "Are you sure you want to delete your account? This action cannot be undone.";
    const password = window.prompt(
      `${confirmMessage}\n\nPlease enter your password to confirm:`,
    );

    if (!password) {
      return; // User cancelled
    }

    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      await axios.delete(`${baseURL}/api/staff/delete-account`, {
        headers,
        data: { password },
      });

      // Clear token and redirect to home
      localStorage.removeItem("ogestyle_token");
      window.location.href = "/";
    } catch (err) {
      if (err?.response?.status === 401) {
        setErrorMsg("Incorrect password");
      } else {
        setErrorMsg(err?.response?.data?.message || "Failed to delete account");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    setErrorMsg("");

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.post(
        `${baseURL}/api/freelancer/stripe/onboard`,
        { businessType: selectedAccountType },
        { headers },
      );

      if (res.data.onboardingUrl) {
        // Redirect to Stripe onboarding
        window.location.href = res.data.onboardingUrl;
      } else {
        setErrorMsg("Failed to create Stripe onboarding link");
      }
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg(
          err?.response?.data?.message || "Failed to start Stripe onboarding",
        );
      }
    } finally {
      setConnectingStripe(false);
    }
  };

  const handleAccessDashboard = async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.post(
        `${baseURL}/api/freelancer/stripe/dashboard-link`,
        {},
        { headers },
      );

      if (res.data.url) {
        window.open(res.data.url, "_blank", "noopener,noreferrer");
      } else {
        setErrorMsg("Failed to create dashboard link");
      }
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message || "Failed to access Stripe dashboard",
      );
    }
  };

  const handleViewPrivacyPolicy = () => {
    window.open(
      "https://www.ogestyle.com/privacy-policy",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const handleSaveBrandName = async () => {
    if (!brandName.trim()) {
      setErrorMsg("Brand name is required");
      return;
    }

    try {
      setSavingBrandName(true);
      setErrorMsg("");
      setSuccessMsg("");

      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.patch(
        `${baseURL}/api/staff/profile/brand-name`,
        { businessName: brandName.trim() },
        { headers },
      );

      setBrandName(res.data?.businessName || brandName.trim());
      setSuccessMsg("Brand name updated successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg(
          err?.response?.data?.message || "Failed to update brand name",
        );
      }
    } finally {
      setSavingBrandName(false);
    }
  };

  const handleDownloadData = async () => {
    setDownloadingData(true);
    setErrorMsg("");

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(`${baseURL}/api/staff/download-data`, {
        headers,
        responseType: "blob",
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `ogestyle-data-${new Date().toISOString().split("T")[0]}.json`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMsg("Your data has been downloaded successfully!");
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        localStorage.removeItem("ogestyle_token");
        navigate("/login");
      } else {
        setErrorMsg(err?.response?.data?.message || "Failed to download data");
      }
    } finally {
      setDownloadingData(false);
    }
  };

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading settings...</p>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  return (
    <FreelancerSidebarLayout>
      <div
        style={{
          maxWidth: "900px",
          margin: "0 auto",
          padding: isMobile ? "15px" : "20px",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "30px" }}>
          <h2
            style={{
              margin: "0 0 8px 0",
              fontSize: isMobile ? "24px" : "28px",
            }}
          >
            Settings
          </h2>
          <p
            style={{
              margin: 0,
              color: "#666",
              fontSize: isMobile ? "14px" : "16px",
            }}
          >
            Manage your account preferences
          </p>
        </div>

        {/* Messages */}
        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
              fontSize: isMobile ? "14px" : "16px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {successMsg && (
          <div
            style={{
              background: "#e6ffed",
              border: "1px solid #03543f",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
              fontSize: isMobile ? "14px" : "16px",
            }}
          >
            <p style={{ margin: 0, color: "#03543f" }}>{successMsg}</p>
          </div>
        )}

        {/* Section Tabs */}
        <div
          style={{
            display: "flex",
            gap: isMobile ? "8px" : "12px",
            marginBottom: "25px",
            flexWrap: "wrap",
            overflowX: isMobile ? "auto" : "visible",
          }}
        >
          {[
            { key: "account", label: "Account" },
            { key: "notifications", label: "Notifications" },
            { key: "privacy", label: "Privacy" },
            { key: "security", label: "Security" },
            { key: "agreements", label: "Agreement" },
            { key: "payments", label: "Payments" },
            { key: "subscription", label: "Subscription" },
          ]
            .filter((section) => {
              // Hide subscription tab for R7 booth renters
              if (
                section.key === "subscription" &&
                window.location.pathname.includes("/booth-renter/")
              ) {
                return false;
              }
              if (section.key === "agreements" && !isBoothRenterPath) {
                return false;
              }
              return true;
            })
            .map((section) => (
              <button
                key={section.key}
                onClick={() => setActiveSection(section.key)}
                style={{
                  padding: isMobile ? "8px 16px" : "10px 20px",
                  border:
                    activeSection === section.key
                      ? "2px solid #001d4a"
                      : "1px solid #ddd",
                  borderRadius: "8px",
                  background:
                    activeSection === section.key ? "#001d4a" : "#fff",
                  color: activeSection === section.key ? "#fff" : "#666",
                  fontSize: isMobile ? "13px" : "14px",
                  fontWeight: activeSection === section.key ? "600" : "500",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {section.label}
              </button>
            ))}
        </div>

        {/* Account Section */}
        {activeSection === "account" && (
          <div
            className="dashboard-card"
            style={{ padding: isMobile ? "20px" : "30px" }}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "20px",
                fontSize: isMobile ? "18px" : "20px",
              }}
            >
              Account Settings
            </h3>
            <div style={{ display: "grid", gap: "20px" }}>
              {/* Email Address - TEMPORARILY HIDDEN */}
              {/* <div
                style={{
                  padding: "15px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "#fafafa",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: isMobile ? "14px" : "15px",
                        marginBottom: "5px",
                      }}
                    >
                      Email Address
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? "13px" : "14px",
                        color: "#333",
                      }}
                    >
                      {userData?.email || "Loading..."}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowEmailChangeModal(true)}
                    style={{
                      padding: "10px 20px",
                      background: "#00509d",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                      transition: "background 0.2s",
                    }}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.background = "#003d7a")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.background = "#00509d")
                    }
                  >
                    Change Email
                  </button>
                </div>
              </div> */}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "15px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: isMobile ? "14px" : "15px",
                      marginBottom: "5px",
                    }}
                  >
                    Public Profile
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#666",
                    }}
                  >
                    Make your profile visible to clients
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={settings.publicProfile || false}
                  onChange={() => handleToggle("publicProfile")}
                  style={{ width: "20px", height: "20px", cursor: "pointer" }}
                />
              </div>

              <div
                style={{
                  padding: "20px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "#fafafa",
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: isMobile ? "14px" : "16px",
                    marginBottom: "6px",
                  }}
                >
                  Brand Name
                </div>
                <div
                  style={{
                    fontSize: isMobile ? "12px" : "13px",
                    color: "#666",
                    marginBottom: "12px",
                  }}
                >
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    flexDirection: isMobile ? "column" : "row",
                  }}
                >
                  <input
                    type="text"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder="Enter your brand name"
                    maxLength={100}
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontSize: "14px",
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSaveBrandName}
                    className="btn-primary"
                    disabled={savingBrandName}
                    style={{
                      padding: "10px 18px",
                      minWidth: "140px",
                    }}
                  >
                    {savingBrandName ? "Saving..." : "Save Brand Name"}
                  </button>
                </div>
              </div>

              {/* Brand Image Upload */}
              <div
                style={{
                  padding: "20px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "#fafafa",
                }}
              >
                <div style={{ marginBottom: "15px" }}>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: isMobile ? "14px" : "16px",
                      marginBottom: "5px",
                    }}
                  >
                    Brand Image <span style={{ color: "#d32f2f" }}>*</span>
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#666",
                      marginBottom: "15px",
                    }}
                  >
                    Upload an image that represents your brand. This will be
                    shown on your public profile. (Required for visibility)
                  </div>
                </div>

                {/* Image Preview */}
                <div
                  style={{
                    marginBottom: "15px",
                    border: "2px dashed #ddd",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "200px",
                  }}
                >
                  {brandImagePreview ? (
                    <img
                      src={brandImagePreview}
                      alt="Brand"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "300px",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#999",
                      }}
                    >
                      <div style={{ fontSize: "48px", marginBottom: "10px" }}>
                        📸
                      </div>
                      <div style={{ fontSize: "14px" }}>
                        No brand image uploaded
                      </div>
                    </div>
                  )}
                </div>

                {/* File Input */}
                <ImageUploadField
                  variant="inline"
                  buttonLabel="Choose Image"
                  uploadOnSelect={false}
                  showPreviews={false}
                  onSelectFiles={(file) => {
                    if (!file) return;
                    setBrandImage(file);
                    setBrandImagePreview(URL.createObjectURL(file));
                  }}
                />

                {/* Upload Buttons */}
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    flexDirection: isMobile ? "column" : "row",
                  }}
                >
                  {brandImage && (
                    <button
                      onClick={async () => {
                        try {
                          setUploadingBrandImage(true);
                          const headers = getAuthHeaders();
                          const baseURL = getApiBaseUrl();
                          if (!headers || !baseURL) return;

                          const formData = new FormData();
                          formData.append("brandImage", await prepareImageForUpload(brandImage));

                          const res = await axios.post(
                            `${baseURL}/api/staff/upload-brand-image`,
                            formData,
                            {
                              headers: {
                                ...headers,
                                "Content-Type": "multipart/form-data",
                              },
                            },
                          );

                          if (res.data.brandImageUrl) {
                            setBrandImagePreview(res.data.brandImageUrl);
                            setBrandImage(null);
                            alert("Brand image uploaded successfully!");
                          }
                        } catch (err) {
                          alert(
                            "Failed to upload brand image. Please try again.",
                          );
                        } finally {
                          setUploadingBrandImage(false);
                        }
                      }}
                      disabled={uploadingBrandImage}
                      className="btn-primary"
                      style={{
                        padding: isMobile ? "10px 20px" : "10px 24px",
                        fontSize: "14px",
                        flex: isMobile ? "1" : "0 0 auto",
                      }}
                    >
                      {uploadingBrandImage ? "Uploading..." : "Upload Image"}
                    </button>
                  )}
                </div>
              </div>

              {/* Save Settings Button */}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="btn-primary"
                  style={{
                    padding: isMobile ? "10px 24px" : "12px 32px",
                    fontSize: isMobile ? "14px" : "15px",
                    width: isMobile ? "100%" : "auto",
                    opacity: saving ? 0.6 : 1,
                    cursor: saving ? "not-allowed" : "pointer",
                  }}
                >
                  {saving ? "Saving..." : "Save Settings"}
                </button>
              </div>

              <div
                style={{
                  padding: "15px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: isMobile ? "14px" : "15px",
                    marginBottom: "10px",
                  }}
                >
                  Profile Information
                </div>
                <button
                  onClick={() => navigate("/dashboard/freelancer/profile/edit")}
                  style={{
                    padding: isMobile ? "8px 16px" : "10px 20px",
                    border: "1px solid #001d4a",
                    borderRadius: "8px",
                    background: "#fff",
                    color: "#001d4a",
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  Edit Profile
                </button>
              </div>

              <div
                style={{
                  padding: "15px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: isMobile ? "14px" : "15px",
                    marginBottom: "10px",
                    color: "#ef4444",
                  }}
                >
                  Danger Zone
                </div>
                <div
                  style={{
                    fontSize: isMobile ? "12px" : "13px",
                    color: "#666",
                    marginBottom: "12px",
                  }}
                >
                  Once you delete your account, there is no going back. Please
                  be certain.
                </div>
                <button
                  onClick={handleDeleteAccount}
                  disabled={saving}
                  style={{
                    padding: isMobile ? "8px 16px" : "10px 20px",
                    border: "1px solid #ef4444",
                    borderRadius: "8px",
                    background: "#fff",
                    color: "#ef4444",
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: "500",
                    cursor: saving ? "not-allowed" : "pointer",
                    opacity: saving ? 0.6 : 1,
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  {saving ? "Deleting..." : "Delete Account"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notifications Section */}
        {activeSection === "notifications" && (
          <div
            className="dashboard-card"
            style={{ padding: isMobile ? "20px" : "30px" }}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "20px",
                fontSize: isMobile ? "18px" : "20px",
              }}
            >
              Notification Preferences
            </h3>
            <div style={{ display: "grid", gap: "15px" }}>
              {[
                {
                  key: "emailNotifications",
                  label: "Email Notifications",
                  desc: "Receive notifications via email",
                },
                {
                  key: "bookingReminders",
                  label: "Booking Reminders",
                  desc: "Get reminded about upcoming bookings",
                },
                {
                  key: "marketingEmails",
                  label: "Marketing Emails",
                  desc: "Receive promotional and marketing emails",
                },
              ].map((item) => (
                <div
                  key={item.key}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "15px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: isMobile ? "14px" : "15px",
                        marginBottom: "5px",
                      }}
                    >
                      {item.label}
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? "12px" : "13px",
                        color: "#666",
                      }}
                    >
                      {item.desc}
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings[item.key] || false}
                    onChange={() => handleToggle(item.key)}
                    style={{ width: "20px", height: "20px", cursor: "pointer" }}
                  />
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: "25px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                className="btn-primary"
                style={{
                  padding: isMobile ? "10px 24px" : "12px 28px",
                  fontSize: isMobile ? "14px" : "15px",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                {saving ? "Saving..." : "Save Preferences"}
              </button>
            </div>
          </div>
        )}

        {/* Privacy Section */}
        {activeSection === "privacy" && (
          <div
            className="dashboard-card"
            style={{ padding: isMobile ? "20px" : "30px" }}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "20px",
                fontSize: isMobile ? "18px" : "20px",
              }}
            >
              Privacy Settings
            </h3>
            <div style={{ display: "grid", gap: "20px" }}>
              <div
                style={{
                  padding: "15px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: isMobile ? "14px" : "15px",
                    marginBottom: "10px",
                  }}
                >
                  Data & Privacy
                </div>
                <div
                  style={{
                    fontSize: isMobile ? "12px" : "13px",
                    color: "#666",
                    marginBottom: "12px",
                  }}
                >
                  Learn about how we collect and use your data.
                </div>
                <button
                  onClick={handleViewPrivacyPolicy}
                  style={{
                    padding: isMobile ? "8px 16px" : "10px 20px",
                    border: "1px solid #001d4a",
                    borderRadius: "8px",
                    background: "#fff",
                    color: "#001d4a",
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  View Privacy Policy
                </button>
              </div>

              <div
                style={{
                  padding: "15px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: isMobile ? "14px" : "15px",
                    marginBottom: "10px",
                  }}
                >
                  Download Your Data
                </div>
                <div
                  style={{
                    fontSize: isMobile ? "12px" : "13px",
                    color: "#666",
                    marginBottom: "12px",
                  }}
                >
                  Request a copy of your personal data including profile,
                  services, bookings, and products.
                </div>
                <button
                  onClick={handleDownloadData}
                  disabled={downloadingData}
                  style={{
                    padding: isMobile ? "8px 16px" : "10px 20px",
                    border: "1px solid #001d4a",
                    borderRadius: "8px",
                    background: downloadingData ? "#e5e7eb" : "#fff",
                    color: downloadingData ? "#9ca3af" : "#001d4a",
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: "500",
                    cursor: downloadingData ? "not-allowed" : "pointer",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  {downloadingData
                    ? "Preparing Download..."
                    : "Download My Data"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Security Section */}
        {activeSection === "security" && (
          <div
            className="dashboard-card"
            style={{ padding: isMobile ? "20px" : "30px" }}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "20px",
                fontSize: isMobile ? "18px" : "20px",
              }}
            >
              Security Settings
            </h3>

            <div style={{ marginBottom: "30px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "15px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  opacity: 0.6,
                  background: "#f9f9f9",
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: isMobile ? "14px" : "15px",
                      marginBottom: "5px",
                    }}
                  >
                    Two-Factor Authentication
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        fontWeight: "400",
                        marginLeft: "8px",
                      }}
                    >
                      (Coming Soon)
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#666",
                    }}
                  >
                    Add an extra layer of security to your account
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={false}
                  disabled
                  style={{
                    width: "20px",
                    height: "20px",
                    cursor: "not-allowed",
                  }}
                />
              </div>
            </div>

            <div>
              <h4
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  marginBottom: "15px",
                }}
              >
                Change Password
              </h4>
              <form
                onSubmit={handleChangePassword}
                style={{ display: "grid", gap: "15px" }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: isMobile ? "13px" : "14px",
                      fontWeight: "500",
                      marginBottom: "8px",
                      color: "#001d4a",
                    }}
                  >
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        currentPassword: e.target.value,
                      })
                    }
                    required
                    style={{
                      width: "100%",
                      padding: isMobile ? "10px" : "12px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: isMobile ? "14px" : "16px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: isMobile ? "13px" : "14px",
                      fontWeight: "500",
                      marginBottom: "8px",
                      color: "#001d4a",
                    }}
                  >
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        newPassword: e.target.value,
                      })
                    }
                    required
                    minLength={8}
                    style={{
                      width: "100%",
                      padding: isMobile ? "10px" : "12px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: isMobile ? "14px" : "16px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: isMobile ? "13px" : "14px",
                      fontWeight: "500",
                      marginBottom: "8px",
                      color: "#001d4a",
                    }}
                  >
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData({
                        ...passwordData,
                        confirmPassword: e.target.value,
                      })
                    }
                    required
                    minLength={8}
                    style={{
                      width: "100%",
                      padding: isMobile ? "10px" : "12px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: isMobile ? "14px" : "16px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn-primary"
                    style={{
                      padding: isMobile ? "10px 24px" : "12px 28px",
                      fontSize: isMobile ? "14px" : "15px",
                      width: isMobile ? "100%" : "auto",
                    }}
                  >
                    {saving ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Payments Section */}
        {activeSection === "payments" && (
          <div
            className="dashboard-card"
            style={{ padding: isMobile ? "20px" : "30px" }}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "20px",
                fontSize: isMobile ? "18px" : "20px",
              }}
            >
              Payment Settings
            </h3>

            {stripeStatus.loading ? (
              <div style={{ padding: "40px", textAlign: "center" }}>
                <p style={{ color: "#666" }}>Loading payment information...</p>
              </div>
            ) : (
              <div style={{ display: "grid", gap: "20px" }}>
                <div
                  style={{
                    padding: "20px",
                    border: "1px solid #dbe4f0",
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%)",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: isMobile ? "15px" : "16px",
                      marginBottom: "8px",
                      color: "#001d4a",
                    }}
                  >
                    Saved Payment Methods
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "13px" : "14px",
                      color: "#4b5563",
                      lineHeight: "1.6",
                      marginBottom: "14px",
                    }}
                  >
                    Manage the saved card used for SMS credit purchases and
                    auto-replenish without leaving settings.
                  </div>
                  <button
                    onClick={() => navigate(paymentMethodsPath)}
                    style={{
                      padding: isMobile ? "10px 18px" : "12px 22px",
                      background: "#00509d",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: isMobile ? "13px" : "14px",
                      fontWeight: "600",
                      width: isMobile ? "100%" : "auto",
                    }}
                  >
                    Open Payment Methods
                  </button>
                </div>

                {/* Stripe Connection Status */}
                <div
                  style={{
                    padding: "20px",
                    border: stripeStatus.connected
                      ? "2px solid #03543f"
                      : "2px solid #fbbf24",
                    borderRadius: "8px",
                    background: stripeStatus.connected ? "#e6ffed" : "#fffbeb",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        background: stripeStatus.connected
                          ? "#03543f"
                          : "#fbbf24",
                      }}
                    />
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: isMobile ? "15px" : "16px",
                        color: "#000",
                      }}
                    >
                      {stripeStatus.connected
                        ? "Stripe Connected"
                        : "Stripe Not Connected"}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "13px" : "14px",
                      color: "#666",
                      marginBottom: "15px",
                    }}
                  >
                    {stripeStatus.connected
                      ? "Your Stripe account is connected and ready to receive payments."
                      : "Connect your Stripe account to start receiving payments from clients."}
                  </div>
                  {stripeStatus.connected && (
                    <div
                      style={{
                        fontSize: isMobile ? "12px" : "13px",
                        color: "#666",
                        marginTop: "10px",
                      }}
                    >
                      {/* Charges are shown only when Stripe enabled them. An R4 is paid by transfer and its
                          account requests `transfers` alone, so a permanent "Charges: Disabled" line described
                          nothing they could act on. */}
                      {stripeStatus.chargesEnabled && (
                        <div>✓ Charges: Enabled</div>
                      )}
                      <div>
                        ✓ Payouts:{" "}
                        {stripeStatus.payoutsEnabled ? "Enabled" : "Disabled"}
                      </div>
                    </div>
                  )}
                </div>

                {!stripeStatus.connected ? (
                  <>
                    {/* Account Type Selection */}
                    <div>
                        <div style={{ marginBottom: "20px" }}>
                          <h4
                            style={{
                              fontSize: isMobile ? "16px" : "17px",
                              marginBottom: "12px",
                            }}
                          >
                            Connect with Stripe
                          </h4>
                          <p
                            style={{
                              fontSize: isMobile ? "13px" : "14px",
                              color: "#666",
                              marginBottom: "20px",
                            }}
                          >
                            To receive payments from your clients, you need to
                            connect a Stripe account. Choose the type of account
                            that best describes your business:
                          </p>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gap: "15px",
                            marginBottom: "25px",
                          }}
                        >
                          {/* Individual Account Option */}
                          <label
                            style={{
                              display: "flex",
                              padding: "18px",
                              border:
                                selectedAccountType === "individual"
                                  ? "2px solid #001d4a"
                                  : "1px solid #e0e0e0",
                              borderRadius: "8px",
                              cursor: "pointer",
                              background:
                                selectedAccountType === "individual"
                                  ? "#f0f4f8"
                                  : "#fff",
                              transition: "all 0.2s ease",
                            }}
                          >
                            <input
                              type="radio"
                              name="accountType"
                              value="individual"
                              checked={selectedAccountType === "individual"}
                              onChange={(e) =>
                                setSelectedAccountType(e.target.value)
                              }
                              style={{
                                marginRight: "12px",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer",
                              }}
                            />
                            <div>
                              <div
                                style={{
                                  fontWeight: "600",
                                  fontSize: isMobile ? "14px" : "15px",
                                  marginBottom: "5px",
                                }}
                              >
                                Individual / Sole Proprietor
                              </div>
                              <div
                                style={{
                                  fontSize: isMobile ? "12px" : "13px",
                                  color: "#666",
                                }}
                              >
                                I'm working as myself, as a freelancer or sole
                                proprietor
                              </div>
                            </div>
                          </label>

                          {/* Company Account Option */}
                          <label
                            style={{
                              display: "flex",
                              padding: "18px",
                              border:
                                selectedAccountType === "company"
                                  ? "2px solid #001d4a"
                                  : "1px solid #e0e0e0",
                              borderRadius: "8px",
                              cursor: "pointer",
                              background:
                                selectedAccountType === "company"
                                  ? "#f0f4f8"
                                  : "#fff",
                              transition: "all 0.2s ease",
                            }}
                          >
                            <input
                              type="radio"
                              name="accountType"
                              value="company"
                              checked={selectedAccountType === "company"}
                              onChange={(e) =>
                                setSelectedAccountType(e.target.value)
                              }
                              style={{
                                marginRight: "12px",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer",
                              }}
                            />
                            <div>
                              <div
                                style={{
                                  fontWeight: "600",
                                  fontSize: isMobile ? "14px" : "15px",
                                  marginBottom: "5px",
                                }}
                              >
                                Registered Business / Company
                              </div>
                              <div
                                style={{
                                  fontSize: isMobile ? "12px" : "13px",
                                  color: "#666",
                                }}
                              >
                                I have a registered business with a business
                                name
                              </div>
                            </div>
                          </label>
                        </div>

                        <button
                          onClick={handleConnectStripe}
                          disabled={connectingStripe}
                          className="btn-primary"
                          style={{
                            padding: isMobile ? "12px 24px" : "14px 32px",
                            fontSize: isMobile ? "14px" : "15px",
                            width: isMobile ? "100%" : "auto",
                          }}
                        >
                          {connectingStripe
                            ? "Connecting..."
                            : "Connect with Stripe"}
                        </button>
                      </div>
                  </>
                ) : (
                  <>
                    {/* Connected - Show Dashboard Access */}
                    <div>
                      <h4
                        style={{
                          fontSize: isMobile ? "16px" : "17px",
                          marginBottom: "12px",
                        }}
                      >
                        Manage Your Stripe Account
                      </h4>
                      <p
                        style={{
                          fontSize: isMobile ? "13px" : "14px",
                          color: "#666",
                          marginBottom: "15px",
                        }}
                      >
                        Access your Stripe Express dashboard to view payments,
                        manage payouts, and update your account settings.
                      </p>
                      <button
                        onClick={handleAccessDashboard}
                        className="btn-primary"
                        style={{
                          padding: isMobile ? "10px 20px" : "12px 24px",
                          fontSize: isMobile ? "13px" : "14px",
                          width: isMobile ? "100%" : "auto",
                        }}
                      >
                        Open Stripe Dashboard
                      </button>
                    </div>

                    {/* Payment Information */}
                    <div
                      style={{
                        padding: "15px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        background: "#f9f9f9",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: isMobile ? "14px" : "15px",
                          marginBottom: "8px",
                        }}
                      >
                        About Payments
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "12px" : "13px",
                          color: "#666",
                          lineHeight: "1.6",
                        }}
                      >
                        <p style={{ margin: "0 0 8px 0" }}>
                          • Payments from clients are processed securely through
                          Stripe
                        </p>
                        <p style={{ margin: "0 0 8px 0" }}>
                          • Funds are transferred directly to your connected
                          bank account
                        </p>
                        <p style={{ margin: "0" }}>
                          • View detailed transaction history and reports in
                          your Stripe dashboard
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Agreements Section (R7 booth renter only) */}
        {activeSection === "agreements" && isBoothRenterPath && (
          <div
            className="dashboard-card"
            style={{ padding: isMobile ? "20px" : "30px" }}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: "20px",
                fontSize: isMobile ? "18px" : "20px",
              }}
            >
              Booth Rental Agreement
            </h3>

            {loadingAgreements ? (
              <div style={{ padding: "20px 0", color: "#64748b" }}>
                Loading agreement...
              </div>
            ) : !selectedAgreement ? (
              <div
                style={{
                  padding: "18px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  background: "#f8fafc",
                  color: "#475569",
                }}
              >
                No signed agreement yet.
              </div>
            ) : (
              <div style={{ display: "grid", gap: "12px" }}>
                <div
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    padding: "14px",
                    background: "#fff",
                  }}
                >
                  <p style={{ margin: "0 0 6px 0" }}>
                    <strong>Status:</strong> {getAgreementStatusLabel(selectedAgreement.status)}
                  </p>
                  <p style={{ margin: "0 0 6px 0" }}>
                    <strong>Billing Cycle:</strong> {selectedAgreement.financialSnapshot?.billingCycle || "-"}
                  </p>
                  <p style={{ margin: 0 }}>
                    <strong>Agreement Date:</strong> {getAgreementDateLabel(selectedAgreement)}
                  </p>
                </div>

                <details>
                  <summary style={{ cursor: "pointer", fontWeight: 600 }}>View Full Agreement</summary>
                  <div
                    style={{
                      marginTop: "10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      padding: "10px",
                      background: "#fff",
                      maxHeight: "320px",
                      overflow: "auto",
                    }}
                    dangerouslySetInnerHTML={{ __html: normalizeAgreementHtml(selectedAgreement.renderedHtml, selectedAgreement.status) }}
                  />
                </details>

                <details>
                  <summary style={{ cursor: "pointer", fontWeight: 600 }}>Agreement Timeline</summary>
                  <ul style={{ marginTop: "10px" }}>
                    {agreementEvents.map((event, index) => (
                      <li key={`${event.event}-${index}`}>
                        {getAgreementEventLabel(event.event)} • {new Date(event.timestamp).toLocaleString()} • {getAgreementActorLabel(event.actorRole)}
                      </li>
                    ))}
                  </ul>
                </details>

                <div style={{ fontSize: "12px", color: "#64748b" }}>
                  Showing {agreements.length} agreement{agreements.length === 1 ? "" : "s"} linked to your booth rentals.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Subscription Section */}
        {activeSection === "subscription" && (
          <div
            className="dashboard-card"
            style={{ padding: isMobile ? "20px" : "30px" }}
          >
            <div
              style={{
                fontSize: isMobile ? "20px" : "24px",
                fontWeight: "700",
                marginBottom: "10px",
                color: "#001d4a",
              }}
            >
              Subscription
            </div>
            <div
              style={{
                fontSize: isMobile ? "13px" : "14px",
                color: "#666",
                marginBottom: "25px",
              }}
            >
              Manage your subscription and billing
            </div>

            {/* Add Payment Method Prompt - Show during trial if not added */}
            {subscriptionData?.isInTrial &&
              !subscriptionData?.paymentMethodCollected && (
                <div
                  style={{
                    padding: isMobile ? "15px" : "20px",
                    background: "#eff6ff",
                    border: "1px solid #3b82f6",
                    borderRadius: "8px",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? "14px" : "15px",
                      fontWeight: "600",
                      color: "#1e40af",
                      marginBottom: "8px",
                    }}
                  >
                    💳 Add a Payment Method (Optional)
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#1e40af",
                      marginBottom: "12px",
                      lineHeight: "1.6",
                    }}
                  >
                    Add a payment method now for seamless continuation after
                    your trial ends. You won't be charged during the trial
                    period.
                  </div>
                  <button
                    onClick={() =>
                      navigate("/dashboard/freelancer/subscription/payment")
                    }
                    style={{
                      padding: isMobile ? "8px 16px" : "10px 20px",
                      border: "none",
                      borderRadius: "6px",
                      background: "#3b82f6",
                      color: "#fff",
                      fontSize: isMobile ? "12px" : "13px",
                      fontWeight: "500",
                      cursor: "pointer",
                    }}
                  >
                    Add Payment Method
                  </button>
                </div>
              )}

            {loadingSubscription ? (
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "#666",
                }}
              >
                Loading subscription information...
              </div>
            ) : subscriptionData ? (
              <div
                style={{
                  background: "#fff",
                  borderRadius: "12px",
                  padding: isMobile ? "15px" : "20px",
                }}
              >
                {/* Current Plan */}
                <div
                  style={{
                    padding: "20px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "15px",
                      flexWrap: "wrap",
                      gap: "10px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: isMobile ? "16px" : "18px",
                          fontWeight: "600",
                          color: "#001d4a",
                        }}
                      >
                        Current Plan
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "13px" : "14px",
                          color: "#666",
                          marginTop: "5px",
                        }}
                      >
                        {subscriptionData.hasSubscription
                          ? subscriptionData.status === "active"
                            ? "Subscription Active"
                            : "Subscription " + subscriptionData.status
                          : subscriptionData.isInTrial
                            ? "Trial Period Active"
                            : "No active subscription"}
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "8px 16px",
                        borderRadius: "20px",
                        background: subscriptionData.hasSubscription
                          ? "#10b981"
                          : subscriptionData.isInTrial
                            ? "#fbbf24"
                            : "#6366f1",
                        color: "#fff",
                        fontSize: isMobile ? "12px" : "13px",
                        fontWeight: "600",
                      }}
                    >
                      {subscriptionData.hasSubscription
                        ? subscriptionData.interval === "year"
                          ? "Annual"
                          : "Monthly"
                        : subscriptionData.isInTrial
                          ? "Trial"
                          : "Subscription Required"}
                    </div>
                  </div>

                  {subscriptionData.hasSubscription && (
                    <>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                          gap: "15px",
                          marginBottom: "15px",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#999",
                              marginBottom: "5px",
                            }}
                          >
                            Amount
                          </div>
                          <div
                            style={{
                              fontSize: isMobile ? "16px" : "18px",
                              fontWeight: "600",
                              color: "#001d4a",
                            }}
                          >
                            ${(subscriptionData.totalMonthly || 0).toFixed(2)}/
                            {subscriptionData.interval === "year"
                              ? "year"
                              : "month"}
                          </div>
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#999",
                              marginBottom: "5px",
                            }}
                          >
                            Next Billing Date
                          </div>
                          <div
                            style={{
                              fontSize: isMobile ? "14px" : "15px",
                              fontWeight: "500",
                              color: "#001d4a",
                            }}
                          >
                            {upcomingInvoice?.nextPaymentAttempt
                              ? new Date(upcomingInvoice.nextPaymentAttempt * 1000).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })
                              : subscriptionData.currentPeriodEnd &&
                                new Date(subscriptionData.currentPeriodEnd).getTime() > 0
                              ? new Date(subscriptionData.currentPeriodEnd).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                })
                              : "—"}
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => {
                          navigate("/dashboard/freelancer/subscription");
                        }}
                        style={{
                          padding: isMobile ? "10px 20px" : "12px 24px",
                          border: "1px solid #001d4a",
                          borderRadius: "8px",
                          background: "#001d4a",
                          color: "#fff",
                          fontSize: isMobile ? "13px" : "14px",
                          fontWeight: "500",
                          cursor: "pointer",
                          width: isMobile ? "100%" : "auto",
                        }}
                      >
                        Manage Subscription
                      </button>
                    </>
                  )}

                  {!subscriptionData.hasSubscription &&
                    !subscriptionData.isInTrial && (
                      <div>
                        <div
                          style={{
                            fontSize: isMobile ? "13px" : "14px",
                            color: "#666",
                            marginBottom: "15px",
                            lineHeight: "1.6",
                          }}
                        >
                          You do not have an active subscription yet. Add a
                          payment method to start your plan and keep billing
                          up to date.
                        </div>
                        <div
                          style={{
                            padding: "15px",
                            background: "#f0fdf4",
                            border: "1px solid #86efac",
                            borderRadius: "8px",
                            marginBottom: "15px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: isMobile ? "13px" : "14px",
                              fontWeight: "600",
                              color: "#166534",
                              marginBottom: "8px",
                            }}
                          >
                            💡 Activate Your Subscription
                          </div>
                          <div
                            style={{
                              fontSize: isMobile ? "12px" : "13px",
                              color: "#166534",
                              lineHeight: "1.6",
                            }}
                          >
                            Start from $
                            {subscriptionData.pricing?.freelancerMonthly || 0}
                            /month. Annual plans save{" "}
                            {subscriptionData.pricing?.annualDiscount || 0}%.
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            navigate("/dashboard/freelancer/billing")
                          }
                          style={{
                            padding: isMobile ? "10px 20px" : "12px 24px",
                            border: "none",
                            borderRadius: "8px",
                            background: "#10b981",
                            color: "#fff",
                            fontSize: isMobile ? "13px" : "14px",
                            fontWeight: "500",
                            cursor: "pointer",
                            width: isMobile ? "100%" : "auto",
                          }}
                        >
                          Add Payment Method
                        </button>
                      </div>
                    )}
                </div>

                {/* Trial Information */}
                {subscriptionData.isInTrial && (
                  <div
                    style={{
                      padding: "20px",
                      border: "1px solid #fbbf24",
                      borderRadius: "8px",
                      background: "#fffbeb",
                      marginBottom: "20px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "15px" : "16px",
                        fontWeight: "600",
                        color: "#92400e",
                        marginBottom: "10px",
                      }}
                    >
                      🎉 Trial Period
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? "13px" : "14px",
                        color: "#92400e",
                        marginBottom: "8px",
                      }}
                    >
                      Your trial ends on{" "}
                      {new Date(
                        subscriptionData.trialEndDate,
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </div>
                    <div
                      style={{
                        fontSize: isMobile ? "12px" : "13px",
                        color: "#92400e",
                      }}
                    >
                      {subscriptionData.trialDaysRemaining > 0
                        ? `${subscriptionData.trialDaysRemaining} days remaining`
                        : "less than 1 day remaining"}
                    </div>
                  </div>
                )}

                {/* Billing Information */}
                <div
                  style={{
                    padding: "15px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    background: "#f9f9f9",
                    marginBottom: "15px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: isMobile ? "14px" : "15px",
                      marginBottom: "8px",
                    }}
                  >
                    Need Help?
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#666",
                      lineHeight: "1.6",
                    }}
                  >
                    <p style={{ margin: "0 0 8px 0" }}>
                      • View invoices and receipts in your email
                    </p>
                    <p style={{ margin: "0 0 8px 0" }}>
                      • Update payment method through the subscription
                      management portal
                    </p>
                    <p style={{ margin: "0" }}>
                      • Contact support for billing questions
                    </p>
                  </div>
                </div>

                {/* Billing & Invoices Link */}
                <button
                  onClick={() => navigate("/dashboard/freelancer/billing")}
                  style={{
                    padding: isMobile ? "12px 20px" : "14px 24px",
                    border: "1px solid #6366f1",
                    borderRadius: "8px",
                    background: "#fff",
                    color: "#6366f1",
                    fontSize: isMobile ? "13px" : "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <span>📄</span>
                  View Billing & Invoices
                </button>
              </div>
            ) : (
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "#666",
                }}
              >
                Unable to load subscription information
              </div>
            )}
          </div>
        )}
      </div>

      {/* Email Change Modal */}
      <EmailChangeModal
        isOpen={showEmailChangeModal}
        onClose={() => setShowEmailChangeModal(false)}
        currentEmail={userData?.email || ""}
        userName={userData?.firstName || "User"}
      />
    </FreelancerSidebarLayout>
  );
}

export default FreelancerSettings;
